import type { DocumentNode } from '@apollo/client';
import { useMutation } from '@apollo/client';
import type { FormikHelpers } from 'formik';

import type {
  TargetingRule,
  TargetingTermValue,
  UpdateAdTargetingValuesMutation,
  UpdateLineItemTargetingValuesMutation,
} from '../../../../apis/graphql';
import { getErrorMessageFromGraphQlErrors, removeTypeNamesFromObject } from '../../../../apis/graphql';
import { writeAdTargetingEmbellishmentFieldsToCache } from '../../../../common/adTargeting';
import { showFailure, showSuccess } from '../../../../common/utils';
import { useTraffickingTableReload } from '../../../../contexts/TraffickingTableReloadContext';
import type { Nullable, TargetingTermValue as TargetingTermValueModel } from '../../../../models';
import { EntityType } from '../../TraffickingPage/hooks/makePageDrawerPlugin';
import { convertValuesForMutation, filterOutCountryTarget } from '../utils/utils';
import { UPDATE_AD_TARGETING_TERM_VALUES } from './queries/updateAdTargetingTermValues';
import { UPDATE_LINE_ITEM_TARGETING_TERM_VALUES } from './queries/updateLineItemTargetingTermValues';
import type { DrawerTargetingTableResult, TargetingTermValuesInput } from './types';
import type { DrawerTargetingFormValues } from './types/formValues';

export type HookProps = {
  entityType: EntityType.AD | EntityType.LINE_ITEM;
  id?: string;
  onSubmitSuccessful: (submittedTermValues: TargetingTermValueModel[]) => Promise<void>;
  /**
   * Parent line item context used to locally compute the ad's targeting "embellishment" fields
   * (`displayPublisherTarget`, `countriesTarget`, `hasInvalidPublisher`, `hasInvalidCountry`) after
   * an ad targeting save. Only required when `entityType === EntityType.AD`; ignored for line item
   * saves. Pass `undefined` when data isn't yet loaded — the cache write is skipped.
   */
  lineItem?: {
    displayPublisherTarget: string[];
    countries: Nullable<string[]>;
  };
  /**
   * Fallback `displayPublisherTarget` when both the ad and line item publisher lists are empty.
   * Typically the ad's current cached value, so we don't wipe a good CampaignSearcher-derived
   * value when only patching flags.
   */
  fallbackAdDisplayPublisherTarget?: string[];
};

type TargetingData = {
  updateAdV5?: {
    targetingTermValues: TargetingTermValue[];
  };
  updateLineItem?: {
    targetingTermValues: TargetingTermValue[];
  };
};

const getTypename = (isAd: boolean): string => {
  return isAd ? 'AdV5' : 'LineItemV5';
};

const getMutation = (isAd: boolean): DocumentNode => {
  return isAd ? UPDATE_AD_TARGETING_TERM_VALUES : UPDATE_LINE_ITEM_TARGETING_TERM_VALUES;
};

const getTargetingTerms = <T extends TargetingData>(isAd: boolean, data: T): TargetingTermValue[] => {
  return isAd ? data?.updateAdV5?.targetingTermValues ?? [] : data?.updateLineItem?.targetingTermValues ?? [];
};

export const useUpdateTargeting = ({
  entityType,
  onSubmitSuccessful,
  id,
  lineItem,
  fallbackAdDisplayPublisherTarget,
}: HookProps): Pick<DrawerTargetingTableResult, 'submitForm' | 'isUpdating'> => {
  const isAd = entityType === EntityType.AD;
  const updateTargetingTermValues = getMutation(isAd);
  const { scheduleTableReload } = useTraffickingTableReload();

  const [updateTargetingValues, { loading }] = useMutation<
    UpdateAdTargetingValuesMutation | UpdateLineItemTargetingValuesMutation
  >(updateTargetingTermValues, {
    update(cache, { data, errors }) {
      if (errors) return;

      const updatedTargetingTermValues = getTargetingTerms(isAd, data as TargetingData);

      const typename = getTypename(isAd);

      const cacheKey = cache.identify({ __typename: typename, id });

      cache.modify({
        id: cacheKey,
        fields: {
          targetingTermValues: () => updatedTargetingTermValues,
        },
      });

      // CampaignSearcher-backed embellishment fields (hasInvalidPublisher / hasInvalidCountry /
      // displayPublisherTarget / countriesTarget) lag the mutation, so derive them locally from
      // the just-saved targeting + the parent line item's constraints and write them via
      // writeFragment. Only applies to ad saves; line-item saves don't touch these ad-level fields.
      if (isAd && id && lineItem) {
        writeAdTargetingEmbellishmentFieldsToCache({
          cache,
          adId: id,
          submittedAdTermValues: updatedTargetingTermValues as TargetingTermValueModel[],
          lineItemDisplayPublisherTarget: lineItem.displayPublisherTarget,
          lineItemCountries: lineItem.countries ?? undefined,
          fallbackAdDisplayPublisherTarget,
        });
      }
    },
  });

  const submitForm = async (
    newValue: TargetingTermValuesInput,
    helpers: FormikHelpers<DrawerTargetingFormValues>,
    rule: TargetingRule | null
  ): Promise<void> => {
    const targetingValueKey = isAd ? 'adTargetingTermValues' : 'lineItemTargetingTermValues';
    const targetingTermValues = newValue[targetingValueKey];

    const { errors } = await updateTargetingValues({
      variables: {
        id,
        newTargetingValues: convertValuesForMutation(targetingTermValues),
        targetingRule: rule ? removeTypeNamesFromObject(rule) : null,
      },
    });

    if (errors) {
      showFailure({
        message: `${isAd ? 'Ad' : 'Line Item'} Save Failed`,
        description: getErrorMessageFromGraphQlErrors(errors),
      });
      const lineItemsNoCountry = filterOutCountryTarget({ value: newValue.lineItemTargetingTermValues });
      helpers.setValues({
        ...newValue,
        lineItemTargetingTermValues: lineItemsNoCountry,
        newTargeting: {
          category: null,
          dimension: null,
          values: [],
        },
      });
      helpers.setSubmitting(false);
    } else {
      showSuccess(isAd);
      // Schedule a delayed refetch so the searcher-applied table (sort order, filters,
      // searcher-only fields) is reconciled after a targeting change.
      scheduleTableReload();
      await onSubmitSuccessful(targetingTermValues);
    }
  };

  return { submitForm, isUpdating: loading };
};
